// src/pages/AuthAction.jsx
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  applyActionCode,
  checkActionCode,
  getAuth,
  verifyPasswordResetCode,
  signOut,
} from "firebase/auth";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { FirebaseError } from "firebase/app";
import * as Sentry from "@sentry/react"; // for React apps

const SYNC_EMAIL_VERIFIED_URL =
  process.env.REACT_APP_SYNC_EMAIL_VERIFIED ||
  "https://us-central1-fluencertuber.cloudfunctions.net/syncEmailVerified";

// Firebase appends continueUrl as a full absolute URL
// (https://collavue.com/dashboard). react-router's navigate() treats that
// string as a literal path, which fell through the catch-all route onto the
// homepage. Reduce it to a same-origin path, and refuse foreign origins.
function toInternalPath(rawUrl, fallback = "/login", depth = 0) {
  if (!rawUrl || depth > 3) return fallback;
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    if (parsed.origin !== window.location.origin) return fallback;
    // Unwrap our own handler: the email settings nest the real destination
    // one level deep as /auth/action?continueUrl=<dest>. Navigating back to
    // /auth/action without an oobCode would just show "Invalid Link".
    if (parsed.pathname === "/auth/action") {
      const nested = parsed.searchParams.get("continueUrl");
      return nested ? toInternalPath(nested, fallback, depth + 1) : fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
  } catch {
    return fallback;
  }
}

async function callSyncEmailVerified(user) {
  try {
    const idToken = await user.getIdToken(true);
    const resp = await fetch(SYNC_EMAIL_VERIFIED_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!resp.ok) {
      const text = await resp.text();
      console.warn(`syncEmailVerified failed: ${resp.status} ${text}`);
    }
  } catch (error) {
    console.warn("syncEmailVerified error:", error);
  }
}

export default function AuthAction() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("working"); // working | success | error
  const [title, setTitle] = useState("Processing…");
  const [buttonNavigation, setButtonNavigation] = useState("/login");
  const [message, setMessage] = useState("");
  const auth = getAuth();
  const ranRef = useRef(false); // prevent double-run in React Strict Mode (dev)

  useEffect(() => {
    if (ranRef.current) return; // Strict Mode guard
    ranRef.current = true;

    let isMounted = true;
    const cleanupRef = { current: null };

    const fail = (t, m) => {
      if (!isMounted) return;
      setStatus("error");
      setTitle(t);
      setMessage(m);
    };
    const succeed = (t, m, redirectTo, delay = 1200) => {
      if (!isMounted) return;
      setStatus("success");
      setTitle(t);
      setMessage(m);
      cleanupRef.current = setTimeout(() => navigate(redirectTo), delay);
    };

    const run = async () => {
      const mode = searchParams.get("mode");
      const actionCode = searchParams.get("oobCode");
      const continueUrl = toInternalPath(searchParams.get("continueUrl"));

      if (!mode || !actionCode)
        return fail("Invalid Link", "Missing action parameters.");

      if (mode === "verifyEmail") {
        setTitle("Verifying Email…");
        try {
          // 1) Read target email (best-effort; ignore non-Firebase noise)
          let targetEmail = "";
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 5000)
            );
            const info = await Promise.race([
              checkActionCode(auth, actionCode),
              timeoutPromise,
            ]);
            targetEmail = info?.data?.email || info?.data?.previousEmail || "";
          } catch (e) {
            if (e instanceof FirebaseError) throw e;
            console.warn("Non-Firebase noise during checkActionCode:", e);
          }

          // 2) If a different user is signed in, sign them out so applyActionCode won't fail spuriously
          const signedInEmail = auth.currentUser?.email || "";
          if (
            signedInEmail &&
            targetEmail &&
            signedInEmail.toLowerCase() !== targetEmail.toLowerCase()
          ) {
            try {
              await signOut(auth);
            } catch {}
          }

          // 3) Apply verification (works even if nobody is signed in)
          try {
            await applyActionCode(auth, actionCode);
          } catch (e) {
            // If code was already consumed, we might still be verified. If signed in, re-check.
            if (
              e?.code === "auth/invalid-action-code" ||
              e?.code === "auth/expired-action-code"
            ) {
              const u = auth.currentUser;
              if (u) {
                try {
                  await u.reload();
                } catch {}
                if (u.emailVerified) {
                  return succeed(
                    "Email Verified!",
                    "Your email is verified.",
                    "/dashboard"
                  );
                }
              }
              // otherwise, bubble to normal error handling
              throw e;
            }
            throw e;
          }

          // 4) If signed in **as the same account**, reload and try to sync
          const user = auth.currentUser;
          if (user) {
            try {
              await user.reload();
            } catch {}
            if (
              !targetEmail ||
              user.email?.toLowerCase() === targetEmail.toLowerCase()
            ) {
              // Run in background - don't await
              callSyncEmailVerified(user).catch((e) =>
                console.warn("Background sync failed:", e)
              );
            }
          }

          return succeed(
            "Email Verified!",
            targetEmail
              ? `Verified ${targetEmail}.`
              : "Your email is verified.",
            user ? "/dashboard" : continueUrl
          );
        } catch (error) {
          console.error("Email verification error:", error);
          const code = error?.code;
          if (code === "auth/expired-action-code") {
            setButtonNavigation("/resend-verification");
            return fail(
              "Verification Failed",
              "This link has expired. Please request a new one."
            );
          }
          if (code === "auth/invalid-action-code") {
            setButtonNavigation("/resend-verification");
            return fail(
              "Verification Failed",
              "This link is invalid or already used. Request a new one."
            );
          }
          if (code === "auth/user-disabled") {
            return fail(
              "Verification Failed",
              "This account has been disabled."
            );
          }
          Sentry.captureException(error, {
            tags: { section: "Verfication Email Failure" },
            extra: { setUser: error },
          });
          return fail(
            "Verification Failed",
            "Failed to verify email. Please try again."
          );
        }
      }

      if (mode === "resetPassword") {
        setTitle("Validating Reset Link…");
        try {
          const email = await verifyPasswordResetCode(auth, actionCode);
          return succeed(
            "Setting up Password Reset prompt. Wait one moment.",
            `Just give us a sec. We found your email: ${email}. Redirecting…`,
            `/reset-password?oobCode=${encodeURIComponent(
              actionCode
            )}&continueUrl=${encodeURIComponent(continueUrl)}`
          );
        } catch (error) {
          console.error("Reset code verify error:", error);
          const code = error?.code;
          if (code === "auth/expired-action-code") {
            setButtonNavigation("/resend-verification");
            return fail(
              "Invalid Reset Link",
              "This password reset link has expired. Request a new one."
            );
          }
          if (code === "auth/invalid-action-code") {
            setButtonNavigation("/resend-verification");
            return fail(
              "Invalid Reset Link",
              "This password reset link is invalid. Request a new one."
            );
          }
          if (code === "auth/user-disabled") {
            return fail(
              "Invalid Reset Link",
              "This account has been disabled."
            );
          }

          // Unknown reason
          Sentry.captureException(error, {
            tags: { section: "Reset password Failure" },
            extra: { setUser: error },
          });
          return fail(
            "Invalid Reset Link",
            "Could not validate reset link. Please try again."
          );
        }
      }

      return fail("Unsupported Action", "The link mode is not supported.");
    };

    run();
    // proper cleanup
    return () => {
      isMounted = false;
      if (cleanupRef.current) clearTimeout(cleanupRef.current);
    };
  }, [searchParams, navigate, auth]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mb-4">
            {status === "working" && (
              <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            {status === "success" && (
              <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8" />
              </div>
            )}
            {status === "error" && (
              <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8" />
              </div>
            )}
          </div>

          <div className="text-lg font-semibold mb-2">{title}</div>
          <div className="text-sm text-slate-600 mb-6">{message}</div>

          <div className="space-y-3">
            {status !== "working" && (
              <button
                onClick={() => navigate(buttonNavigation)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
              >
                Continue
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 text-center text-xs text-slate-500">
          Having trouble?{" "}
          <a
            href="mailto:mrjustinyoung991@gmail.com"
            className="text-blue-600 hover:text-blue-700"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}
